// src/pages/hr/recruitment/HiringRequests.jsx
// ============================================================
// HR Recruitment: Hiring Requests & Requisitions Module
// Enhanced with Green Theme and Clean Outline Icons
// ============================================================

import React, { useState, useMemo } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { approveRecruitmentRequest, rejectRecruitmentRequest, createJobOpening } from "../../../hooks/useRecruitment";
import {
  useHiringRequisitions,
  approveHiringRequisition,
  rejectHiringRequisition,
  convertRequisitionToJob,
  updateHiringRequisition,
} from "../../../hooks/useHiringRequisitions";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-hot-toast";
import {
  Briefcase,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Check,
} from "lucide-react";

export default function HiringRequests() {
  const { recruitmentRequests, requestsLoading } = useRecruitmentContext();
  const { data: requisitions, loading: reqsLoading } = useHiringRequisitions();
  const { userProfile } = useAuth();
  const hrName = userProfile?.displayName || userProfile?.name || "HR Admin";
  const hrId = userProfile?.uid || userProfile?.id || "";

  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  // Combine both collections into a unified list
  const combinedRequisitions = useMemo(() => {
    const list = [...requisitions];

    // Include legacy recruitmentRequests if not already in requisitions
    recruitmentRequests.forEach((legacy) => {
      if (!list.some((r) => r.id === legacy.id)) {
        list.push({
          id: legacy.id,
          title: legacy.positionTitle || legacy.title || "Position",
          department: legacy.department || "General",
          openings: Number(legacy.openings || 1),
          employmentType: legacy.employmentType || "Full-Time",
          priority: legacy.priority || "Medium",
          experience: legacy.experience || "",
          salaryRange: legacy.salaryRange || "",
          justification: legacy.justification || "",
          skills: legacy.skills || [],
          requestedByName: legacy.requestedByName || "Team Lead",
          requestedByRole: legacy.requestedByRole || "Team Lead",
          requestedDate: legacy.createdAt?.toDate ? legacy.createdAt.toDate().toISOString() : legacy.requestedDate || "",
          status: legacy.status || "Submitted",
          isLegacy: true,
        });
      }
    });

    return list.filter((r) => {
      const role = (r.requestedByRole || r.createdRole || "Team Lead").toLowerCase();
      const matchRole =
        roleFilter === "all" ||
        (roleFilter === "admin" && role.includes("admin")) ||
        (roleFilter === "lead" && (role.includes("lead") || role.includes("team")));

      const status = r.status || "Submitted";
      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "Submitted" && (status === "Submitted" || status === "Pending")) ||
        status === statusFilter;

      return matchRole && matchStatus;
    });
  }, [requisitions, recruitmentRequests, roleFilter, statusFilter]);

  // Handle Approving Requisition & Converting to Job Opening
  async function handleApproveAndConvert(req) {
    const title = req.title || req.positionTitle || "Position";
    if (!window.confirm(`Approve requisition for "${title}" and create active Job Opening?`)) return;

    try {
      if (req.isLegacy) {
        await approveRecruitmentRequest(req.id, hrId, hrName);
        await createJobOpening({
          title,
          department: req.department || "Engineering",
          openings: Number(req.openings || 1),
          vacancies: Number(req.openings || 1),
          employmentType: req.employmentType || "Full-Time",
          priority: req.priority || "Medium",
          experienceRequired: req.experience || "",
          salaryRange: req.salaryRange || "",
          hiringManager: req.requestedByName || "Team Lead",
          description: req.justification || "",
          skillsRequired: req.skills || [],
          status: "Open",
          createdByName: hrName,
        });
      } else {
        await approveHiringRequisition(req.id, { hrId, hrName, hrRemarks: "Approved by HR" });
        await convertRequisitionToJob(req, hrName);
      }

      toast.success(`Requisition "${title}" approved and active Job Opening created!`);
    } catch (err) {
      toast.error("Failed to approve requisition: " + err.message);
    }
  }

  // Handle Requesting Changes
  async function handleRequestChanges(req) {
    const feedback = window.prompt(`Request changes for "${req.title || req.positionTitle}". Enter feedback / required revisions:`);
    if (feedback === null || !feedback.trim()) return;

    try {
      if (!req.isLegacy) {
        await updateHiringRequisition(req.id, {
          status: "Changes Requested",
          hrReviewedById: hrId,
          hrReviewedByName: hrName,
          hrRemarks: feedback,
        });
      } else {
        await rejectRecruitmentRequest(req.id, hrId, hrName, `Changes Requested: ${feedback}`);
      }
      toast.success(`Revision feedback sent to requester for "${req.title || req.positionTitle}".`);
    } catch (err) {
      toast.error("Failed to request changes: " + err.message);
    }
  }

  // Handle Rejecting Requisition
  async function handleReject(req) {
    const reason = window.prompt(`Reject requisition for "${req.title || req.positionTitle}". Enter rejection reason:`);
    if (reason === null || !reason.trim()) return;

    try {
      if (!req.isLegacy) {
        await rejectHiringRequisition(req.id, { hrId, hrName, hrRemarks: reason });
      } else {
        await rejectRecruitmentRequest(req.id, hrId, hrName, reason);
      }
      toast.success(`Requisition "${req.title || req.positionTitle}" rejected.`);
    } catch (err) {
      toast.error("Failed to reject requisition: " + err.message);
    }
  }

  const loading = requestsLoading && reqsLoading;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .hr-header-banner {
          background: #1b4b31;
          border-radius: 16px;
          padding: 1.35rem 1.75rem;
          color: #ffffff;
          box-shadow: 0 4px 20px rgba(27, 75, 49, 0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .hr-req-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 1.4rem;
          box-shadow: 0 3px 12px rgba(15, 23, 42, 0.03);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.1rem;
          transition: all 0.2s ease;
        }
        .hr-req-card:hover {
          border-color: #10b981;
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.08);
        }
        .req-green-pill {
          padding: 0.28rem 0.75rem;
          border-radius: 99px;
          font-size: 0.75rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
        }
        .btn-green-primary {
          background: #1b4b31;
          color: #ffffff;
          border: none;
          border-radius: 10px;
          padding: 0.55rem 1.1rem;
          font-weight: 700;
          font-size: 0.82rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          box-shadow: 0 2px 8px rgba(27, 75, 49, 0.2);
          transition: all 0.2s ease;
        }
        .btn-green-primary:hover {
          background: #143724;
          transform: translateY(-1px);
        }
        .btn-outline-action {
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          padding: 0.5rem 0.85rem;
          font-weight: 700;
          font-size: 0.78rem;
          color: #475569;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.18s ease;
        }
        .btn-outline-action:hover {
          background: #f8fafc;
          border-color: #94a3b8;
        }
      `}</style>

      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem",
        marginBottom: "1.25rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <Briefcase size={24} strokeWidth={1.8} color="#1b4b31" />
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, letterSpacing: "-0.02em", color: "#1b4b31" }}>
            Hiring Requisitions & Requests
          </h1>
        </div>
      </div>

      {/* Filter & Subtab Controls */}
      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "0.85rem 1.1rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          {/* Role Filter */}
          <select
            className="ats-select"
            style={{ borderRadius: 10, fontSize: "0.84rem", padding: "0.5rem 0.85rem", minWidth: 170, border: "1.5px solid #cbd5e1" }}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Requesters</option>
            <option value="lead">Team Leads</option>
            <option value="admin">Admin Panel</option>
          </select>

          {/* Status Filter */}
          <select
            className="ats-select"
            style={{ borderRadius: 10, fontSize: "0.84rem", padding: "0.5rem 0.85rem", minWidth: 180, border: "1.5px solid #cbd5e1" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="Submitted">Pending HR Review</option>
            <option value="Approved">Approved</option>
            <option value="Converted">Converted to Job</option>
            <option value="Changes Requested">Changes Requested</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#475569" }}>
          Total Requisitions: <span style={{ color: "#15803d", fontWeight: 800 }}>{combinedRequisitions.length}</span>
        </div>
      </div>

      {/* Requisitions List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "3.5rem", color: "#64748b" }}>
          <div className="hrms-spinner" style={{ margin: "0 auto 1rem" }} />
          Loading requisitions...
        </div>
      ) : combinedRequisitions.length === 0 ? (
        <div style={{ background: "#ffffff", border: "2px dashed #cbd5e1", borderRadius: 16, padding: "3.5rem 2rem", textAlign: "center", color: "#64748b" }}>
          <Briefcase className="w-10 h-10 text-slate-300" strokeWidth={1.8} style={{ margin: "0 auto 0.75rem" }} />
          <h4 style={{ margin: "0 0 0.4rem", fontWeight: 700, color: "#334155" }}>No Hiring Requisitions Found</h4>
          <p style={{ margin: 0, fontSize: "0.82rem" }}>No requisitions match your filter criteria.</p>
        </div>
      ) : (
        <div>
          {combinedRequisitions.map((req) => {
            const roleBadge = req.requestedByRole || req.createdRole || "Team Lead";
            const isSubmitted = req.status === "Submitted" || req.status === "Pending";
            const skillsArr = Array.isArray(req.skills || req.skillsRequired)
              ? (req.skills || req.skillsRequired)
              : typeof (req.skills || req.skillsRequired) === "string"
              ? (req.skills || req.skillsRequired).split(",").map((s) => s.trim())
              : [];

            const formattedDate = req.requestedDate
              ? new Date(req.requestedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : req.createdAt?.toDate
              ? req.createdAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : "Recent";

            return (
              <div key={req.id} className="hr-req-card">
                {/* Header Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                  <div>
                    <div style={{ fontSize: "1.18rem", fontWeight: 800, color: "#0f172a" }}>
                      {req.title || req.positionTitle}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600, marginTop: 4, display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
                      <span>
                        Requested By: <strong style={{ color: "#1e293b" }}>{req.requestedByName || req.createdByName || "User"}</strong> ({roleBadge})
                      </span>
                      <span>Department: <strong style={{ color: "#1e293b" }}>{req.department || "General"}</strong></span>
                      <span>Vacancies: <strong style={{ color: "#1e293b" }}>{req.openings || req.vacancies || 1}</strong></span>
                      <span>Date: <strong style={{ color: "#1e293b" }}>{formattedDate}</strong></span>
                    </div>
                  </div>

                  <span className="req-green-pill">
                    <CheckCircle2 size={13} strokeWidth={2} />
                    {req.status === "Submitted" ? "Pending HR Review" : req.status === "Converted" ? "Converted to Job" : req.status}
                  </span>
                </div>

                {/* Requirements Chips - All Green Unified Theme */}
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", fontSize: "0.76rem" }}>
                  <span className="req-green-pill">
                    {req.employmentType || "Full-Time"}
                  </span>
                  <span className="req-green-pill">
                    Priority: {req.priority || "Medium"}
                  </span>
                  {(req.experience || req.experienceRequired) && (
                    <span className="req-green-pill">
                      Exp: {req.experience || req.experienceRequired}
                    </span>
                  )}
                  {req.salaryRange && (
                    <span className="req-green-pill">
                      Budget: {req.salaryRange}
                    </span>
                  )}
                </div>

                {/* Justification Box */}
                {(req.justification || req.description) && (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "3.5px solid #10b981", borderRadius: 10, padding: "0.8rem 1rem", fontSize: "0.82rem", color: "#334155" }}>
                    <div style={{ fontWeight: 700, color: "#166534", fontSize: "0.72rem", marginBottom: 3, letterSpacing: "0.03em" }}>
                      BUSINESS JUSTIFICATION
                    </div>
                    <div>{req.justification || req.description}</div>
                  </div>
                )}

                {/* Skills tags */}
                {skillsArr.length > 0 && (
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    {skillsArr.map((sk, idx) => (
                      <span key={idx} style={{ background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", padding: "0.2rem 0.55rem", borderRadius: 6, fontSize: "0.74rem", fontWeight: 600 }}>
                        {sk}
                      </span>
                    ))}
                  </div>
                )}

                {/* HR Feedback / Remarks */}
                {req.hrRemarks && (
                  <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "0.65rem 0.85rem", fontSize: "0.78rem" }}>
                    <span style={{ fontWeight: 700, color: "#15803d" }}>
                      HR Feedback ({req.hrReviewedByName || "HR"}):
                    </span>{" "}
                    {req.hrRemarks}
                  </div>
                )}

                {/* Card Actions Toolbar */}
                <div style={{ display: "flex", gap: "0.6rem", justifyContent: isSubmitted ? "flex-end" : "space-between", alignItems: "center", marginTop: "0.4rem", flexWrap: "wrap" }}>
                  {isSubmitted && (
                    <>
                      <button
                        className="btn-outline-action"
                        style={{ color: "#dc2626", borderColor: "#fca5a5" }}
                        onClick={() => handleReject(req)}
                      >
                        <XCircle size={14} strokeWidth={1.8} /> Reject
                      </button>

                      <button
                        className="btn-outline-action"
                        onClick={() => handleRequestChanges(req)}
                      >
                        <AlertCircle size={14} strokeWidth={1.8} /> Request Changes
                      </button>

                      <button
                        className="btn-green-primary"
                        onClick={() => handleApproveAndConvert(req)}
                      >
                        <CheckCircle2 size={15} strokeWidth={2} /> Approve & Create Job Opening
                      </button>
                    </>
                  )}

                  {(req.status === "Approved" || req.status === "Converted") && (
                    <>
                      <span style={{ fontSize: "0.78rem", color: "#15803d", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                        <Check size={14} strokeWidth={2.5} /> Approved by {req.hrReviewedByName || req.approvedByName || "HR Admin"}
                      </span>
                      {req.status === "Approved" && (
                        <button
                          className="btn-green-primary"
                          style={{ padding: "0.4rem 0.9rem", fontSize: "0.78rem" }}
                          onClick={() => handleApproveAndConvert(req)}
                        >
                          <Briefcase size={14} strokeWidth={1.8} /> Create Job Opening
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
